<template>
  <div class="push-banner">
    <button
      v-if="visible"
      class="push-banner-trigger"
      type="button"
      @click="openDialog"
    >
      <i class="material-icons">notifications_active</i>
      <span class="hidden-sm hidden-xs">فعّل إشعارات المتصفح</span>
    </button>

    <!-- Testing aid — clears our own local bookkeeping so the trigger
         button above reappears immediately, without a reload. -->
    <button
      class="push-banner-reset"
      type="button"
      title="إعادة تعيين حالة إشعارات المتصفح (للاختبار)"
      @click="resetPushState"
    >
      <i class="material-icons">restart_alt</i>
    </button>

    <!-- Deliberately outside the v-if above: closing/hiding the trigger the
         moment a token is acquired must not also rip an open dialog out of
         the DOM before the user has read the result inside it. -->
    <md-dialog :md-active.sync="dialogOpen" class="push-dialog">
      <md-dialog-title class="push-dialog-title">
        <md-icon class="title-icon">notifications_active</md-icon>
        إشعارات المتصفح
      </md-dialog-title>
      <md-dialog-content class="push-dialog-content">
        <template v-if="!success">
          <p>
            الرجاء التأكيد على استلام إشعارات من المتصفح، سيقوم المتصفح بالتأكيد
            مرة أخرى على قبولك.
          </p>
          <p class="hint">
            يمكنك إلغاء وصول الإشعارات من إعدادات المتصفح في أي وقت.
          </p>
        </template>

        <div v-if="errorMessage" class="push-error">{{ errorMessage }}</div>

        <div v-if="success" class="push-success">
          <p>
            <b>تم تفعيل الإشعارات بنجاح.</b> ستصلك تنبيهات النظام على المتصفح.
          </p>
          <!-- Temporary debug aid — remove once the Chrome/Android delivery
               issue is diagnosed. Lets a tester copy the full FCM token
               directly from the phone (long-press → copy) for a manual
               Firebase Console "Send test message" test, without needing
               USB debugging / remote DevTools. -->
          <p v-if="lastToken" class="hint token-hint">
            توكن الجهاز (مؤقت للاختبار — اضغط مطولاً للنسخ):<br />
            <code class="token-value">{{ lastToken }}</code>
          </p>
        </div>
      </md-dialog-content>
      <md-dialog-actions class="push-dialog-actions">
        <template v-if="!success">
          <md-button :disabled="loading" @click="cancelDialog">
            إلغاء
          </md-button>
          <md-button class="md-accent" :disabled="loading" @click="decline">
            رفض
          </md-button>
          <md-button class="md-rose" :disabled="loading" @click="enable">
            <md-progress-spinner
              v-if="loading"
              class="md-white"
              :md-diameter="16"
              :md-stroke="2"
              md-mode="indeterminate"
            />
            <span v-else>تفعيل</span>
          </md-button>
        </template>
        <md-button v-else class="md-rose" @click="closeAfterResult">
          تم
        </md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import {
  enablePushNotifications,
  registerDeviceTokenIfNeeded,
  listenForegroundMessages,
  REGISTERED_TOKEN_KEY,
  REGISTERED_SUBSCRIBER_KEY,
  DISMISS_KEY,
  REGISTERED_NOVU_APP_KEY,
  CURRENT_NOVU_APP_ID,
} from "@/services/push-notifications";

export default {
  name: "PushNotificationBanner",
  data() {
    return {
      dialogOpen: false,
      loading: false,
      errorMessage: null,
      permissionState: null,
      // Mirrored into real Vue-reactive state — Vue has no way to know when
      // a plain localStorage.getItem() call inside a computed should be
      // re-read, so reading it directly there silently goes stale.
      dismissed: false,
      // Mirrors REGISTERED_TOKEN_KEY, i.e. "the backend has confirmed this
      // device" — not just "Firebase handed us a token". Getting a token
      // can succeed while the backend call right after it fails (network
      // blip, subscriberId not ready yet), and in that case this must stay
      // false so the trigger button reappears and the user can retry.
      tokenAcquired: false,
      foregroundListenerStarted: false,
      success: false,
      // Temporary debug aid — see the note near its use in the template.
      lastToken: null,
    };
  },
  computed: {
    subscriberId() {
      return this.$store.getters["global/profile"]?.id || null;
    },
    visible() {
      // Permission being "granted" only means the browser-level prompt was
      // answered — it does NOT mean we actually got a working token (that
      // step can still fail separately, e.g. a network/VPN block). So the
      // banner stays available to retry until a token is actually acquired,
      // and only permanently hides once permission is explicitly denied or
      // the user declined our own dialog.
      return (
        typeof Notification !== "undefined" &&
        this.permissionState !== "denied" &&
        !this.dismissed &&
        !this.tokenAcquired
      );
    },
  },
  mounted() {
    if (typeof Notification !== "undefined") {
      this.permissionState = Notification.permission;
    }
    // Stored as the declining subscriber's id (not just "1") — a decline
    // from one user on a shared browser must not suppress the banner for a
    // different user who later logs in on the same device.
    this.dismissed =
      localStorage.getItem(DISMISS_KEY) === String(this.subscriberId);
    // The stored token belongs to a subscriber, not just to this browser —
    // a different logged-in user on the same device must not inherit the
    // previous user's "already registered" state. If subscriberId isn't
    // loaded yet, this correctly comes out false and the silent sync below
    // (once permission is granted) settles it for real.
    // Likewise, a registration made under another Novu environment doesn't count.
    this.tokenAcquired =
      !!localStorage.getItem(REGISTERED_TOKEN_KEY) &&
      localStorage.getItem(REGISTERED_SUBSCRIBER_KEY) ===
        String(this.subscriberId) &&
      localStorage.getItem(REGISTERED_NOVU_APP_KEY) === CURRENT_NOVU_APP_ID;
    this.watchPermissionChanges();

    // Permission already granted from a previous visit (the normal case on
    // every login after the first).
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "granted"
    ) {
      // Attach the foreground-message listener FIRST, independently of the
      // token sync. syncTokenWithBackend() also calls startForegroundListener(),
      // but only *after* enablePushNotifications() -> getToken() — a network
      // round-trip to Google that can be slow or outright blocked (VPN /
      // firewall). If that throws, the listener would never attach and a
      // notification arriving while the tab is open would be dropped silently,
      // even though permission is granted and the SW is already registered.
      this.startForegroundListener();

      // Separately, silently re-confirm the backend has our current token in
      // case it rotated. registerDeviceTokenIfNeeded() is a no-op network-wise
      // when the token hasn't actually changed; this call is allowed to fail
      // quietly.
      this.syncTokenWithBackend({ silent: true });
    }
  },
  methods: {
    watchPermissionChanges() {
      // Brave (and some other browsers) can grant notifications for a limited
      // duration and silently revert to "default" once it expires — no reload
      // happens, so we listen live instead of only checking once on mount.
      if (!navigator.permissions || !navigator.permissions.query) return;
      navigator.permissions
        .query({ name: "notifications" })
        .then((status) => {
          this.permissionState = status.state;
          status.onchange = () => {
            this.permissionState = status.state;
          };
        })
        .catch(() => {});
    },
    openDialog() {
      this.errorMessage = null;
      this.success = false;
      this.lastToken = null;
      this.dialogOpen = true;
    },
    // Polls the store instead of failing on the first empty read — the
    // profile fetch that populates global/profile can still be in flight,
    // especially over a slower/tunneled mobile connection.
    waitForSubscriberId(timeoutMs) {
      if (this.subscriberId) return Promise.resolve();
      return new Promise((resolve) => {
        const start = Date.now();
        const check = () => {
          if (this.subscriberId || Date.now() - start >= timeoutMs) {
            resolve();
            return;
          }
          setTimeout(check, 300);
        };
        check();
      });
    },
    // Testing convenience — clears our own localStorage bookkeeping AND
    // the matching reactive flags directly, so the trigger button reappears
    // immediately without a page reload (a plain localStorage.removeItem()
    // alone wouldn't do that — Vue has no way to know it changed).
    resetPushState() {
      localStorage.removeItem(REGISTERED_TOKEN_KEY);
      localStorage.removeItem(REGISTERED_SUBSCRIBER_KEY);
      localStorage.removeItem(REGISTERED_NOVU_APP_KEY);
      localStorage.removeItem(DISMISS_KEY);
      this.tokenAcquired = false;
      this.dismissed = false;
      this.errorMessage = null;
      this.success = false;
      this.lastToken = null;
      this.foregroundListenerStarted = false;
    },
    closeAfterResult() {
      this.dialogOpen = false;
      this.success = false;
    },
    // Just closes the dialog with no decision recorded — the banner will
    // show again next time, unlike decline() which is a permanent "no".
    cancelDialog() {
      this.dialogOpen = false;
    },
    decline() {
      localStorage.setItem(DISMISS_KEY, String(this.subscriberId));
      this.dismissed = true;
      this.dialogOpen = false;
    },
    async enable() {
      this.loading = true;
      this.errorMessage = null;
      this.success = false;
      try {
        await this.syncTokenWithBackend({ silent: false });
        // Dialog stays open on purpose — shows the success message until
        // the user dismisses it with "تم".
      } catch (error) {
        this.errorMessage =
          error.message || "حدث خطأ غير متوقع، يرجى المحاولة مرة أخرى";
        this.permissionState =
          typeof Notification !== "undefined" ? Notification.permission : null;
      } finally {
        this.loading = false;
      }
    },
    // Shared by the explicit "enable" click and the silent per-login
    // recheck — gets the current token then tells the backend about it,
    // skipping the network call if that exact token is already registered.
    async syncTokenWithBackend({ silent }) {
      try {
        // Checked BEFORE touching Firebase on purpose — a missing
        // subscriberId (profile not loaded yet, race on a slow mobile
        // connection) should fail fast here rather than after we've
        // already gone through the Firebase permission/token flow.
        // Instead of failing immediately, wait a few seconds for it —
        // over a tunnel/mobile connection the profile fetch can still be
        // in flight when the user reaches this button.
        await this.waitForSubscriberId(5000);
        if (!this.subscriberId) {
          throw new Error(
            "لا يوجد مستخدم مسجّل الدخول حاليًا، يرجى المحاولة مرة أخرى بعد قليل"
          );
        }

        const token = await enablePushNotifications();
        this.permissionState =
          typeof Notification !== "undefined" ? Notification.permission : null;
        this.startForegroundListener();

        await registerDeviceTokenIfNeeded(token, this.subscriberId);
        // Only flip this once the backend has actually confirmed the
        // device — flipping it right after getting the Firebase token
        // (as this used to do) would hide the trigger button even when
        // the registerDeviceTokenIfNeeded call above fails, leaving the
        // user stuck with no way to retry.
        this.tokenAcquired = true;
        this.success = true;
        if (!silent) {
          this.lastToken = token;
        }
      } catch (error) {
        if (silent) {
          console.warn("[push] silent token sync failed:", error.message);
          return;
        }
        throw error;
      }
    },
    // Firebase delivers a message differently depending on whether the tab
    // has focus: a closed/background tab is handled by the service worker
    // (firebase-messaging-sw.js) automatically, but a FOREGROUND tab gets
    // the message handed to the page's JS instead — nothing shows on
    // screen unless we display it ourselves, which is what this does.
    //
    // Uses ServiceWorkerRegistration.showNotification() rather than
    // `new Notification(...)` — on Android, browsers reject the plain
    // Notification constructor from page JS ("Illegal constructor. Use
    // ServiceWorkerRegistration.showNotification() instead"), so the old
    // `new Notification()` call silently failed on Android Chrome even
    // though the message had arrived. showNotification() works the same
    // way on both desktop and mobile.
    startForegroundListener() {
      if (this.foregroundListenerStarted) return;
      this.foregroundListenerStarted = true;
      listenForegroundMessages((payload) => {
        const title = payload.notification?.title || "إشعار جديد";
        const body = payload.notification?.body || "";
        navigator.serviceWorker.ready
          .then((registration) =>
            registration.showNotification(title, {
              body,
              icon: "/favicon.png",
              // Same message can also land in firebase-messaging-sw.js's
              // onBackgroundMessage handler (focus/background detection
              // isn't perfectly exclusive) — tagging both with Firebase's
              // own per-message id means the browser treats a duplicate as
              // a replacement instead of a second, separate notification.
              tag: payload.messageId,
            })
          )
          .catch((error) => {
            console.warn(
              "[push] failed to show foreground notification:",
              error
            );
          });
      }).catch((error) => {
        // Reset so a later call (from syncTokenWithBackend, or the next page
        // load) can retry attaching the listener instead of being blocked by
        // the flag we optimistically set above.
        this.foregroundListenerStarted = false;
        console.warn("[push] foreground listener failed to start:", error);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.push-banner {
  display: inline-flex;
  align-items: center;
}

.push-banner-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid rgba(233, 30, 99, 0.4);
  color: inherit;
  border-radius: 999px;
  padding: 6px 12px;
  margin-inline-end: 10px;
  cursor: pointer;
  font-size: 13px;

  i {
    font-size: 18px;
  }

  &:hover {
    background: rgba(233, 30, 99, 0.08);
  }
}

.push-banner-reset {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.15);
  color: inherit;
  opacity: 0.6;
  border-radius: 50%;
  cursor: pointer;

  i {
    font-size: 16px;
  }

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.06);
  }
}

// Below 991px the text label hides (.hidden-sm / .hidden-xs in the
// template) — the pill's horizontal padding was sized for icon+text, so
// without the text it read as an oversized circle around a tiny icon.
// TopNavbar.vue gives every direct child of .md-collapse a fixed 36x36 slot
// — that assumed one icon per item, but .push-banner now holds two buttons
// (trigger + reset), so it needs `!important` here to grow back out of that
// box instead of squeezing both into it.
@media (max-width: 991px) {
  .push-banner {
    width: auto !important;
    height: auto !important;
    gap: 2px;
  }

  .push-banner-trigger {
    padding: 0;
    margin-inline-end: 0;
    width: 32px !important;
    height: 32px !important;
    justify-content: center;
    border-radius: 50%;

    i {
      font-size: 18px;
    }
  }

  .push-banner-reset {
    width: 24px !important;
    height: 24px !important;

    i {
      font-size: 14px;
    }
  }
}
</style>

<style lang="scss">
.push-dialog {
  direction: rtl;

  .push-dialog-title {
    display: flex;
    align-items: center;
    gap: 10px;

    .title-icon {
      margin-inline-end: 4px;
    }
  }

  .push-dialog-content p {
    margin: 0 0 12px;
    line-height: 1.7;
  }

  .push-dialog-content .hint {
    font-size: 13px;
    color: #888;
  }

  .push-error {
    margin-top: 12px;
    padding: 10px 14px;
    background: #fbe7e5;
    color: #ac382f;
    border-radius: 6px;
    font-size: 13.5px;
  }

  .push-success {
    padding: 10px 14px;
    background: #e2f5ec;
    color: #157f52;
    border-radius: 6px;
    font-size: 13.5px;
  }

  .token-hint {
    margin-top: 10px !important;
  }

  .token-value {
    direction: ltr;
    display: block;
    word-break: break-all;
    background: rgba(0, 0, 0, 0.06);
    padding: 6px 8px;
    border-radius: 4px;
    font-size: 11.5px;
    user-select: all;
  }

  .push-dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 12px 16px;
  }
}
</style>
